import type { CellClassParams, EditableCallbackParams, ITooltipParams } from 'ag-grid-community';
import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { useAppSelector } from 'hooks/useAppSelector';
import camelCase from 'lodash-es/camelCase';
import { useMemo, useRef } from 'react';
import { MessageTooltip } from 'routes/secondary-market-batch/grid/components/message-tooltip';
import { getHigherLevel } from 'routes/secondary-market-batch/grid/utils/get-higher-level';
import { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import { Row, RowData, SecondaryBatchColumnDefinition } from 'state/workspaces/secondary-market/batch/model';
import { selectColumnsDefinitions, selectRowsByOrderType } from 'state/workspaces/secondary-market/batch/selectors';
import { ApiLevel } from 'utils/http/httpClient.model';

function getValidations(col: SecondaryBatchColumnDefinition, row: RowData | undefined) {
  if (!col.field || !row?.rowId) {
    return undefined;
  }

  return row.metadata?.messageByColumns?.[camelCase(col.field)] ?? [];
}

function getEditableState(rows: readonly Row[], rowId: string | undefined, field: keyof RowData) {
  if (!rowId) {
    return undefined;
  }

  return rows.find(row => row.data.rowId === rowId)?.editableState?.[field];
}

function isUpfrontFeeEditableTradeWay(tradeWay: unknown) {
  return tradeWay === 'Buy' || tradeWay === 'ClientBuy';
}

function isUpfrontFeeColumn(orderType: OrderType, col: SecondaryBatchColumnDefinition) {
  return orderType === 'ebiz-quote' && col.field === 'isUpfrontFeeIncluded';
}

export function useColumnDefinitions(orderType: OrderType): IBlotterColumnDefinition<RowData>[] {
  const columnDefinitions = useAppSelector(s => selectColumnsDefinitions(s, orderType));
  const rows = useAppSelector(s => selectRowsByOrderType(s, orderType));
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  return useMemo(
    () =>
      columnDefinitions.map(col => ({
        ...col,
        ...(isUpfrontFeeColumn(orderType, col) ? { type: 'upfrontFee' } : {}),
        ...(col.filterable ? {} : { filter: false }),
        editable: (params: EditableCallbackParams<RowData>) => {
          const overrideIsEditableForUpfrontFee = isUpfrontFeeColumn(orderType, col)
            ? isUpfrontFeeEditableTradeWay(params.data?.tradeWay)
            : col.editable;

          return (
            (overrideIsEditableForUpfrontFee &&
              !params.data?.metadata?.isReadonly &&
              params.data?.modifyStatus !== 'loading' &&
              params.data?.operationStatus !== 'InProgress') ??
            false
          );
        },
        tooltipComponent: MessageTooltip,
        tooltipValueGetter: ({ data }: ITooltipParams<RowData, string[]>) => {
          const validationMessages = getValidations(col, data);

          const editableState = getEditableState(rowsRef.current, data?.rowId, col.field as keyof RowData);

          return { validationMessages, editableState };
        },
        cellClassRules: {
          ...(col.cellClassRules ?? {}),
          'secondary-market-batch-validation-cell--warning': ({
            data,
          }: CellClassParams<RowData, RowData[keyof RowData]>) => {
            const errorLevel = getHigherLevel(getValidations(col, data));

            const editableState = getEditableState(rowsRef.current, data?.rowId, col.field as keyof RowData);

            const editableStateErrorLevel: ApiLevel | undefined = editableState?.errors
              ? 'Error'
              : editableState?.warnings
                ? 'Warning'
                : undefined;

            const isWarningLevel =
              (errorLevel !== 'Info' && errorLevel !== undefined) || editableStateErrorLevel === 'Warning';

            return isWarningLevel;
          },
          'secondary-market-batch-validation-cell--danger': ({
            data,
          }: CellClassParams<RowData, RowData[keyof RowData]>) => {
            const editableState = getEditableState(rowsRef.current, data?.rowId, col.field as keyof RowData);

            const editableStateErrorLevel: ApiLevel | undefined = editableState?.errors
              ? 'Error'
              : editableState?.warnings
                ? 'Warning'
                : undefined;

            return editableStateErrorLevel === 'Error';
          },
        },
      })),
    [columnDefinitions, orderType],
  );
}
